/**
 * RBAC type definitions and action/view/field constants.
 * Constants use SCREAMING_SNAKE_CASE; types/interfaces use an `I` prefix.
 */

import { IUserManagementMode } from '@/consts/userManagement';
import { ILicenseManagementMode } from '@/consts/organizationManagement';
import { PlatformRoleType } from '@/services/users/types';

/** HoloXR role assigned to the current viewer (from JWT). */
export type IAuthRole = PlatformRoleType;

export type IAccessQualifier = 'none' | 'self' | 'scoped' | 'full';

export type IVisibilityQualifier = 'hidden' | 'selfVisible' | 'scopedVisible' | 'visible';

export type IUiViewAccess = 'none' | 'scoped' | 'full' | 'limited';

/** All permission actions evaluated by `canPerformAction`. */
export const PERMISSION_ACTION = {
  viewUserDirectory: 'viewUserDirectory',
  viewUserDetails: 'viewUserDetails',
  editUserProfile: 'editUserProfile',
  transferUserToOrganization: 'transferUserToOrganization',
  deleteUserAccount: 'deleteUserAccount',
  modifyPlatformRoles: 'modifyPlatformRoles',
  createOrganization: 'createOrganization',
  viewOrganizationProfile: 'viewOrganizationProfile',
  updateOrganizationProfile: 'updateOrganizationProfile',
  updateOrganizationBilling: 'updateOrganizationBilling',
  updateOrganizationSettings: 'updateOrganizationSettings',
  deleteOrganization: 'deleteOrganization',
  viewOrganizationUsers: 'viewOrganizationUsers',
  inviteUserToOrganization: 'inviteUserToOrganization',
  resendInvitation: 'resendInvitation',
  removeUserFromOrganization: 'removeUserFromOrganization',
  assignOrgAdminRole: 'assignOrgAdminRole',
  assignOrgOwnerRole: 'assignOrgOwnerRole',
  assignPlatformRole: 'assignPlatformRole',
  viewLicense: 'viewLicense',
  viewOrganizationLicensePool: 'viewOrganizationLicensePool',
  assignLicenseToOrgUser: 'assignLicenseToOrgUser',
  viewScheduleHoursToOrgUser: 'viewScheduleHoursToOrgUser',
  assignScheduleHoursToOrgUser: 'assignScheduleHoursToOrgUser',
  assignLicenseToIndependentUser: 'assignLicenseToIndependentUser',
  distributeLicensesToOrganization: 'distributeLicensesToOrganization',
  requestMoreLicenses: 'requestMoreLicenses',
  requestLicenseRenewal: 'requestLicenseRenewal',
  requestLicenseUpgrade: 'requestLicenseUpgrade',
} as const;

export type IPermissionAction = (typeof PERMISSION_ACTION)[keyof typeof PERMISSION_ACTION];

/** Admin UI views (pages/sections) gated by `canAccessView`. */
export const UI_VIEW = {
  userDetailsPage: 'userDetailsPage',
  userDirectory: 'userDirectory',
  organizationProfilePage: 'organizationProfilePage',
  organizationSettingsPage: 'organizationSettingsPage',
  organizationUsersTable: 'organizationUsersTable',
  organizationsTable: 'organizationsTable',
  independentUsersTable: 'independentUsersTable',
} as const;

export type IUiView = (typeof UI_VIEW)[keyof typeof UI_VIEW];

/** User profile field bundles (U1–U6) driving API field requests and column visibility. */
export const USER_FIELD_BUNDLE = {
  identity: 'U1_identity',
  contact: 'U2_contact',
  phone: 'U3_phone',
  lifecycle: 'U4_lifecycle',
  authMetadata: 'U5_authMetadata',
  systemFields: 'U6_systemFields',
} as const;

export type IUserFieldBundle = (typeof USER_FIELD_BUNDLE)[keyof typeof USER_FIELD_BUNDLE];

/** Organization profile field bundles (O1–O6) driving API field requests and column visibility. */
export const ORGANIZATION_FIELD_BUNDLE = {
  identity: 'O1_identity',
  billing: 'O2_billing',
  settings: 'O3_settings',
  metadata: 'O4_metadata',
  lifecycle: 'O5_lifecycle',
  systemFields: 'O6_systemFields',
} as const;

export type IOrganizationFieldBundle = (typeof
  ORGANIZATION_FIELD_BUNDLE)[keyof typeof ORGANIZATION_FIELD_BUNDLE];

/** Optional context used to resolve scoped/self permissions and field visibility. */
export interface IViewerContext {
  targetUserUuid?: string;
  organizationUuid?: string;
  viewerOrganizationUuid?: string;
  isViewingOwnRecord?: boolean;
  userManagementMode?: IUserManagementMode;
  licenseManagementMode?: ILicenseManagementMode;
}
